import React, { useMemo } from 'react'
import { generatePath } from 'react-router'
import { Field } from 'react-final-form'
import { noop } from 'lodash'
import styled from 'styled-components'

import { useListRequest, useRequest } from '../hooks/request'
import { useUser } from '../data/UserContext'
import { FACM_STATUS, DRAFTLESS_STATUSES, DRAFTLESS_STATUSES_OPTIONS } from '../constants/facmStatus'
import { RACE_TYPE_OPTIONS } from '../constants/raceType'
import { facmList, facmCancel } from '../requests/facm'
import { dateToDateTime } from '../utils/dateToDDMMYYYY'
import { PATHS } from '../constants/paths'
import { useInterval } from '../utils/useInterval'
import { theme } from '../constants/theme'
import { getFullName } from '../utils/getFullName'
import { roleToStatusMap } from '../constants/roleToStatusMap'
import { VARIANT_SECONDARY } from '../constants/variants'
import { tagsColor } from '../constants/status'
import { ROLES } from '../constants/roles'

import { PageTitle } from './common/PageTitle'
import { PageContent } from './layout/layout'
import { List } from './common/List'
import { Trad } from './common/Trad'
import { Form } from './common/Form'
import { Body, H3 } from './common/Typography'
import { Margin } from './common/Margin'
import { SelectField, UserField } from './fields/SelectField'
import { ListSearch } from './common/ListSearch'
import { Tag } from './common/Tag'
import { ArchiveButton } from './common/ConfirmButton'
import { DummyButton } from './common/Button'


const getRowLink = (item) => generatePath(PATHS.FACM_DETAIL, { id: item.id })

const facmNumber = { title: 'facmNumber' }

const createFACMList = ({
  statusFilter, getStatusFilter,
  customColor,
  negativeFilter, titles, displayItem, pageTitle, displayFilters = false, autoRefetch, startSort,
}) => {
  const delay = 60

  return () => {
    const { user } = useUser()
    const finalTitles = [
      facmNumber,
      ...titles,
      ...(user.role === ROLES.ADMIN ? [{ size: 0, content: <HiddenButton /> }] : []),
    ]
    const shouldFilter = statusFilter || getStatusFilter
    let startFilter = null
    if (user.role === ROLES.UNIT_COMMANDER) {
      startFilter = useMemo(() => (shouldFilter ? {
        facmStatus: statusFilter || getStatusFilter({ user }),
        cduId: user.id,
      } : {
        cduId: user.id,
      }), [shouldFilter, user])
    } else {
      startFilter = useMemo(() => (shouldFilter ? {
        facmStatus: statusFilter || getStatusFilter({ user }),
      } : { }), [shouldFilter, user])
    }

    const facmListRequest = useListRequest({
      startFilter,
      negativeFilter,
      startSort: startSort || '-facmNumber',
      start: true,
      request: facmList,
    })
    const facmCancelRequest = useRequest({
      request: facmCancel,
      handler: (p) => p.then(facmListRequest.fetch),
    })
    useInterval({ action: autoRefetch && facmListRequest.fetch, delay })

    return (
      <>
        <PageTitle tradId={pageTitle} />
        <PageContent>
          {autoRefetch && (
            <div>
              <Body tradId="autoRefetch" values={{ delay }} />
            </div>
          )}
          <Margin v={20} />
          {displayFilters && (
            <Form onSubmit={noop}>
              {({ form }) => (
                <FilterWrapper>
                  <Filter>
                    <H3 tradId="searchTitle" bold variant={VARIANT_SECONDARY} />
                    <Margin v={10} />
                    <ListSearch
                      placeholder="searchField"
                      onSearch={facmListRequest.setSearch}
                    />
                  </Filter>
                  <Margin h={20} />
                  <Filters>
                    <Filter>
                      <H3 tradId="filterTitle" bold variant={VARIANT_SECONDARY} />
                      <Margin v={10} />
                      <FieldFilter
                        component={SelectField}
                        isClearable
                        name="filter.facmStatus"
                        placeholder="filterStatusPlaceholder"
                        translate
                        isSearchable={false}
                        onChange={(value) => {
                          if (!value) {
                            form.change('filter.facmStatus', '')

                            return facmListRequest.setFilter({
                              facmStatus: DRAFTLESS_STATUSES,
                            })
                          }
                          form.change('filter.facmStatus', value)
                          form.change('filter.authorId', '')
                          form.change('filter.raceType', '')
                          if (value !== 'WAITING') {
                            facmListRequest.setFilter({
                              facmStatus: [value],
                            })
                          } else {
                            facmListRequest.setFilter({
                              facmStatus: [
                                FACM_STATUS.WAITING_CORPS_CHIEF,
                                FACM_STATUS.WAITING_MOUNTAIN_OFFICER,
                                FACM_STATUS.WAITING_UNIT_COMMANDER,
                              ],
                            })
                          }
                        }}
                        options={DRAFTLESS_STATUSES_OPTIONS}
                      />
                    </Filter>
                    <Margin h={20} />
                    <Filter>
                      <FieldFilter
                        component={UserField}
                        name="filter.authorId"
                        onChange={(authorId) => {
                          if (!authorId) {
                            form.change('filter.authorId', '')

                            return facmListRequest.setFilter({
                              authorId: undefined,
                            })
                          }
                          form.change('filter.authorId', authorId)
                          form.change('filter.facmStatus', '')
                          form.change('filter.raceType', '')
                          facmListRequest.setFilter({
                            authorId,
                          })
                        }}
                        placeholder="userFilterPlaceholder"
                      />
                    </Filter>
                    <Margin h={20} />
                    <Filter>
                      <FieldFilter
                        component={SelectField}
                        isClearable
                        name="filter.raceType"
                        placeholder="filterRaceTypePlaceholder"
                        translate
                        isSearchable={false}
                        onChange={(value) => {
                          if (!value) {
                            form.change('filter.raceType', '')

                            return facmListRequest.setFilter({
                              raceType: undefined,
                            })
                          }
                          form.change('filter.raceType', value)
                          form.change('filter.authorId', '')
                          form.change('filter.facmStatus', '')
                          facmListRequest.setFilter({
                            raceType: value,
                          })
                        }}
                        options={RACE_TYPE_OPTIONS}
                      />
                    </Filter>
                  </Filters>
                </FilterWrapper>
              )}
            </Form>
          )}
          <List
            getRowLink={getRowLink}
            user={user}
            onConfirmArchive={facmCancelRequest.fetch}
            archievingId={facmCancelRequest.state.params?.id}
            customColor={customColor}
            listRequest={facmListRequest}
            columnTitles={finalTitles}
            displayItem={displayItem}
          />
        </PageContent>
      </>
    )
  }
}

const FilterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  @media (max-width: 1000px) {
    margin-left: 10px;
  }
`

const FieldFilter = styled(Field)`
  background: ${theme.colors.white};
  padding: 10px;
  border-radius: 5px;
  margin: 0px;
`

const Filter = styled.div`
  display: flex;
  flex-direction: column;
`

const Filters = styled.div`
  display: flex;
  flex-direction: row;
  padding-top: 10px;
  align-items: flex-end;
  justify-content: space-between;
  &>* {
    flex: 1;
  }
  @media (max-width: 1000px) {
    flex-direction: column;
    align-items: stretch;
  }
`

const HiddenButton = styled(DummyButton)`
  padding: 3px;
  visibility: hidden;
`

// titles
const activityFrame = { title: 'activityFrame' }
const instructor = { title: 'instructor' }
const state = { title: 'state' }
const callRescueTime = { title: 'callRescueTime' }
const raceDates = { title: 'raceDates' }
const raceDateTime = { title: 'raceDateTime' }

// cells
const displayRaceNumber = (item) => item.facmNumber

const displayActivityFrame = (item) => ({
  content: (
    <Trad id={`race_${item.activityFrame}`} />
  ),
})
const displayStartDate = (item) => dateToDateTime(item.startDate)
const displayRaceDates = (item) => `${dateToDateTime(item.startDate)} <-> ${dateToDateTime(item.endDate)}`
const displayGDH = (item) => dateToDateTime(item.gdh)
const displayStatus = (item) => ({
  content: (
    <Tag label={`facmStatus.${item.status}`} color={tagsColor[item.status]} filled />
  ),
})
const displayIntructor = (item) => {
  const author = item.author ? getFullName({ user: item.author }) : '-'

  return author
}

export const FACMListAll = createFACMList({
  pageTitle: 'facmAll',
  displayFilters: true,
  negativeFilter: {
    status: [FACM_STATUS.DRAFT],
    cduId: useUser.id,
  },
  titles: [
    activityFrame,
    instructor,
    state,
    raceDateTime,
  ],

  displayItem: (item, props) => [
    displayRaceNumber(item),
    displayActivityFrame(item),
    displayIntructor(item),
    displayStatus(item),
    displayStartDate(item),
    ...(props.user.role === ROLES.ADMIN ? [
      ...(item.status !== 'CANCELLED' ? [
        {
          content: (
            <ArchiveButton
              titleTrad="confirmArchiveFacm"
              onConfirm={() => props.onConfirmArchive({ id: item.id })}
            />
          ),
        },
      ] : [
        { size: 0, content: <HiddenButton /> },
      ]),
    ] : []
    )],
})

export const FACMListPending = createFACMList({
  pageTitle: 'facmPending',
  getStatusFilter: ({ user }) => [roleToStatusMap[user.role]],
  titles: [
    activityFrame,
    instructor,
    raceDateTime,
  ],

  displayItem: (item) => [
    displayRaceNumber(item),
    displayActivityFrame(item),
    displayIntructor(item),
    displayStartDate(item),
  ],
})

const customColor = (item) => {
  const now = Date.now()
  if (item.status === FACM_STATUS.ONGOING && item.endDate < now) {
    if (item.gdh < now) {
      return theme.colors.red
    }

    return theme.colors.warningOrange
  }

  return 'transparent'
}

const customColorToCome = (item) => {
  const now = Date.now()
  if (item.startDate < now) {
    return theme.colors.warningYellow
  }

  return 'transparent'
}

export const FACMListOngoing = createFACMList({
  pageTitle: 'facmOngoing',
  autoRefetch: true,
  customColor,
  startSort: 'endDate',
  statusFilter: [FACM_STATUS.ONGOING],
  titles: [
    instructor,
    raceDates,
    callRescueTime,
  ],

  displayItem: (item) => [
    displayRaceNumber(item),
    displayIntructor(item),
    displayRaceDates(item),
    displayGDH(item),
  ],
})

export const FACMListPlanned = createFACMList({
  pageTitle: 'facmPlanned',
  customColor: customColorToCome,
  statusFilter: [FACM_STATUS.APPROVED],
  titles: [
    activityFrame,
    instructor,
    raceDateTime,
  ],

  displayItem: (item) => [
    displayRaceNumber(item),
    displayActivityFrame(item),
    displayIntructor(item),
    displayStartDate(item),
  ],
})
